import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react'
import type { AccountInfo } from '@azure/msal-browser'
import { environment } from '@/app/core/config/environment'
import { isB2CEnabled, loginRequest, msalInstance } from './msalConfig'
import { assertMockAuthNotInProduction } from './mockAuthGuard'
import { acquireApiToken } from './authToken'
import { normalizeRoles, type Role } from './useRoles'

export interface AuthUser {
  id: string
  email: string
  name: string
  roles: Role[]
  tenantId: string
  initials: string
}

interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  /** True when running without real B2C (VITE_MOCK_AUTH / missing config). */
  mockMode: boolean
  /** Start the B2C redirect. Pass an email `loginHint` (from an invite link) to prefill it. */
  login: (loginHint?: string) => void
  logout: () => void
  getToken: () => Promise<string | null>
  /** Mock-mode only: swap the signed-in user's roles to test the role-aware UI. */
  setMockRoles?: (roles: Role[]) => void
  /**
   * Authoritative roles resolved from the backend (adm_user_roles). B2C injects
   * the enriched `roles` claim into the ACCESS token (which the API reads) but
   * not always the ID token the SPA reads — so the SPA cannot trust id-token
   * roles. AdminContext calls this once `currentUser` loads so all UI gating
   * (RoleGuard, sidebar, canWrite/canOperate) uses the real DB roles.
   */
  setResolvedRoles: (roles: Role[]) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function initialsFrom(name: string, email: string): string {
  const source = name?.trim() || email?.trim() || '?'
  const parts = source.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return source.slice(0, 2).toUpperCase()
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}

/* ───────────────────────────── Mock provider (dev) ───────────────────────── */

const MOCK_ROLES_KEY = 'xlops.mockRoles'
const DEFAULT_MOCK_ROLES: Role[] = [
  'ADMIN',
  'OPS_MANAGER',
  'WAREHOUSE_USER',
  'FINANCE',
  'SALES_REP',
  'SALES_MANAGER',
  'PROCUREMENT_MANAGER',
  'PROCUREMENT_USER',
  'AUDITOR',
]

function readMockRoles(): Role[] {
  if (typeof window === 'undefined') return DEFAULT_MOCK_ROLES
  const stored = window.localStorage.getItem(MOCK_ROLES_KEY)
  if (!stored) return DEFAULT_MOCK_ROLES
  const parsed = normalizeRoles(stored.split(','))
  return parsed.length > 0 ? parsed : DEFAULT_MOCK_ROLES
}

function MockAuthProvider({ children }: { children: ReactNode }) {
  const [roles, setRoles] = useState<Role[]>(() => readMockRoles())

  const setMockRoles = useCallback((next: Role[]) => {
    setRoles(next)
    window.localStorage.setItem(MOCK_ROLES_KEY, next.join(','))
  }, [])

  const value = useMemo<AuthContextValue>(() => {
    const name = 'Dev Admin'
    const email = 'dev-admin@xlops.local'
    return {
      user: {
        id: '00000000-0000-0000-0000-000000000001',
        email,
        name,
        roles,
        tenantId: '00000000-0000-0000-0000-000000000001',
        initials: initialsFrom(name, email),
      },
      isAuthenticated: true,
      isLoading: false,
      mockMode: true,
      login: () => {},
      logout: () => {},
      getToken: async () => null,
      setMockRoles,
      // Mock roles are authoritative in dev; ignore backend-resolved roles.
      setResolvedRoles: () => {},
    }
  }, [roles, setMockRoles])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/* ───────────────────────────── Real provider (B2C) ───────────────────────── */

function userFromAccount(account: AccountInfo | null): AuthUser | null {
  if (!account) return null
  const claims = (account.idTokenClaims ?? {}) as Record<string, unknown>
  const id = String(claims.sub ?? claims.oid ?? account.localAccountId ?? '')
  const emailClaim = claims.email ?? (Array.isArray(claims.emails) ? claims.emails[0] : undefined)
  const email = String(emailClaim ?? account.username ?? '')
  const name = String(claims.name ?? account.name ?? email)
  // B2C user flows surface custom attributes with an extension_ prefix; accept both. A delimited
  // string (multi-role packed into a single B2C string attribute) is split here.
  const rolesClaim = claims.roles ?? claims.extension_roles
  const rawRoles = Array.isArray(rolesClaim)
    ? (rolesClaim as string[])
    : typeof rolesClaim === 'string'
      ? rolesClaim.split(/[,;\s]+/)
      : []
  const tenantId = String(claims.tenant_id ?? claims.extension_tenant_id ?? '')
  return {
    id,
    email,
    name,
    roles: normalizeRoles(rawRoles),
    tenantId,
    initials: initialsFrom(name, email),
  }
}

function RealAuthProvider({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  // Roles resolved from the backend (adm_user_roles) — authoritative over the
  // id-token `roles` claim, which B2C does not reliably populate.
  const [resolvedRoles, setResolvedRoles] = useState<Role[] | null>(null)

  const account = instance.getActiveAccount() ?? accounts[0] ?? null

  const value = useMemo<AuthContextValue>(() => {
    const baseUser = userFromAccount(account)
    const user = baseUser ? { ...baseUser, roles: resolvedRoles ?? baseUser.roles } : null
    return {
      user,
      isAuthenticated,
      isLoading: inProgress !== 'none',
      mockMode: false,
      login: (loginHint?: string) => {
        // Always use the combined sign-up-or-sign-in policy so tokens (and their
        // refresh tokens) stay in ONE B2C policy realm. A separate sign-up policy
        // mints refresh tokens that cannot be redeemed at this policy's token endpoint
        // (B2C returns 400), which breaks silent token refresh. Invite links still
        // auto-redirect here (with the email prefilled); the invitee uses "Sign up now".
        void instance.loginRedirect(loginHint ? { ...loginRequest, loginHint } : loginRequest)
      },
      logout: () => {
        void instance.logoutRedirect({
          account: account ?? undefined,
          postLogoutRedirectUri: environment.b2c.postLogoutRedirectUri,
        })
      },
      getToken: acquireApiToken,
      setResolvedRoles,
    }
    // account identity is captured via homeAccountId; inProgress/auth drive re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.homeAccountId, isAuthenticated, inProgress, instance, resolvedRoles])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/* ───────────────────────────── Misconfiguration (fail closed) ─────────────── */

function AuthConfigError() {
  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-background">
      <div className="max-w-md text-center space-y-3">
        <h1 className="text-lg font-bold text-foreground">Authentication not configured</h1>
        <p className="text-sm text-muted-foreground">
          This build runs with real sign-in (<code>VITE_MOCK_AUTH=false</code>) but no Azure AD B2C
          client id is set. For safety the app does <strong>not</strong> fall back to a mock admin.
          Set <code>VITE_B2C_CLIENT_ID</code> (and the other <code>VITE_B2C_*</code> values), or set
          <code> VITE_MOCK_AUTH=true</code> for local development.
        </p>
      </div>
    </div>
  )
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Defense-in-depth: even if the boot-time guard in main.tsx is bypassed, never render
  // the mock provider in a production bundle.
  assertMockAuthNotInProduction(import.meta.env.PROD, environment.mockAuth)
  // Explicit dev bypass only. Mock mode must be opted into, never inferred.
  if (environment.mockAuth) {
    return <MockAuthProvider>{children}</MockAuthProvider>
  }
  // Real-auth build but B2C is not configured: fail closed (no silent mock admin).
  if (!isB2CEnabled || !msalInstance) {
    return <AuthConfigError />
  }
  return (
    <MsalProvider instance={msalInstance}>
      <RealAuthProvider>{children}</RealAuthProvider>
    </MsalProvider>
  )
}
